import {
  InventoryBalanceItem,
  TimelineEventItem,
  OcrInvoiceResult,
  OcrShiftReportResult,
  OcrTransferResult,
  OcrWasteResult,
  OcrInventoryBalanceResult,
  ShiftReconciliationResult,
  ShiftSalesItem,
  ShiftWasteItem,
  ShiftStockCountItem,
  IngredientItem,
  ProductItem,
  BomItem,
  ConversionItem,
  AuthUser,
  LoginResponse,
  UpdateProfileResponse,
  ChangePasswordResponse,
} from './types';


// Detect API Base URL: Vite local dev uses proxy (''), production requires VITE_API_URL
const CONFIGURED_API_URL = (import.meta.env.VITE_API_URL || '').trim();
const IS_LOCAL_DEV =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '');

function getApiBase(): string {
  // 1. Khi đang chạy Dev Server cục bộ (localhost / 127.0.0.1): luôn dùng relative path ''
  // để Vite dev server tự động reverse-proxy sang Local Worker (http://127.0.0.1:8787) đọc file .dev.vars
  if (IS_LOCAL_DEV) {
    return '';
  }

  // 2. Khi chạy Production (trên Cloudflare Pages hoặc domain thật): bắt buộc đọc VITE_API_URL
  if (CONFIGURED_API_URL) {
    return CONFIGURED_API_URL.replace(/\/+$/, '');
  }

  throw new Error('Chưa cấu hình biến môi trường VITE_API_URL (URL của Cloudflare Worker Backend).');
}

const API_BASE = getApiBase();

const TOKEN_STORAGE_KEY = 'bar_ingredient_auth_token';

export const authStorage = {
  getToken: (): string | null => {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  },
  setToken: (token: string): void => {
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch {}
  },
  clearToken: (): void => {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {}
  },
};

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const token = authStorage.getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401 && endpoint !== '/api/auth/login') {
      authStorage.clearToken();
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    let errMessage = `HTTP ${response.status}`;
    try {
      const errBody = await response.json();
      errMessage = errBody.message || errBody.error || errMessage;
    } catch {}
    throw new Error(errMessage);
  }

  const json = await response.json();
  return json;
}

export const api = {
  // Authentication & Authorization
  login: async (credentials: { username: string; password: string }): Promise<LoginResponse> => {
    const res = await request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    if (res.success && res.token) {
      authStorage.setToken(res.token);
    }
    return res;
  },

  getCurrentUser: async (): Promise<AuthUser> => {
    const res = await request<{ success: boolean; user: AuthUser }>('/api/auth/me');
    return res.user;
  },

  updateProfile: async (data: { name: string }): Promise<UpdateProfileResponse> => {
    const res = await request<UpdateProfileResponse>('/api/auth/profile', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (res.success && res.token) {
      authStorage.setToken(res.token);
    }
    return res;
  },

  changePassword: async (data: { currentPassword: string; newPassword: string }): Promise<ChangePasswordResponse> => {
    return await request<ChangePasswordResponse>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  logout: (): void => {

    authStorage.clearToken();
    window.dispatchEvent(new CustomEvent('auth:logout'));
  },

  getToken: (): string | null => authStorage.getToken(),

  // Master Data
  getMasterData: async (refresh = false) => {
    const res = await request<{
      success: boolean;
      data: {
        products: ProductItem[];
        ingredients: IngredientItem[];
        boms: BomItem[];
        conversions: ConversionItem[];
      };
    }>(`/api/master-data${refresh ? '?refresh=true' : ''}`);
    return res.data;
  },

  saveMasterData: async (payload: {
    products: ProductItem[];
    ingredients: IngredientItem[];
    boms: BomItem[];
    conversions: ConversionItem[];
  }) => {
    const res = await request<{
      success: boolean;
      unchanged?: boolean;
      message: string;
      stats: any;
    }>('/api/sync/sheets?force=true', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return res;
  },

  // Inventory
  getInventory: async (warehouse?: 'CENTRAL' | 'STORE') => {
    const query = warehouse ? `?warehouse=${warehouse}` : '';
    const res = await request<{ success: boolean; data: InventoryBalanceItem[] }>(`/api/inventory${query}`);
    return res.data;
  },

  // Timeline
  getTimeline: async (limit = 20, beforeId?: number, search?: string, eventType?: string) => {
    const params = new URLSearchParams();
    params.set('limit', limit.toString());
    if (beforeId) params.set('before_id', beforeId.toString());
    if (search && search.trim()) params.set('search', search.trim());
    if (eventType && eventType !== 'ALL') params.set('type', eventType);

    const res = await request<{
      success: boolean;
      data: TimelineEventItem[];
      has_more?: boolean;
      next_cursor?: number;
    }>(`/api/timeline?${params.toString()}`);
    return res;
  },

  // Flow 1: Nhập hàng Kho Tổng
  scanGoodsInwardOcr: async (imageBase64: string, mimeType = 'image/jpeg') => {
    const res = await request<{ success: boolean; result: OcrInvoiceResult }>('/api/ocr/goods-inward', {
      method: 'POST',
      body: JSON.stringify({ image_base64: imageBase64, mime_type: mimeType }),
    });
    return res.result;
  },

  confirmGoodsInward: async (items: Array<{ ingredient_name: string; package_unit: string; quantity: number; amount?: number }>, note?: string) => {
    const res = await request<{ success: boolean; result: any }>('/api/inventory/inward', {
      method: 'POST',
      body: JSON.stringify({ items, note }),
    });
    return res.result;
  },

  // Flow 2: Chuyển kho Tổng -> Cửa hàng
  scanStockTransferOcr: async (imageBase64: string, mimeType = 'image/jpeg') => {
    const res = await request<{ success: boolean; result: OcrTransferResult }>('/api/ocr/stock-transfer', {
      method: 'POST',
      body: JSON.stringify({ image_base64: imageBase64, mime_type: mimeType }),
    });
    return res.result;
  },

  transferStock: async (items: Array<{ ingredient_name: string; quantity: number; unit?: string }>, note?: string) => {
    const res = await request<{ success: boolean; result: any }>('/api/inventory/transfer', {
      method: 'POST',
      body: JSON.stringify({ items, note }),
    });
    return res.result;
  },

  // Flow 3: Chốt ca & Cân bằng kho (Hỗ trợ 1 hoặc nhiều ảnh chụp nối tiếp)
  scanShiftReportOcr: async (
    imagesInput: string | Array<{ base64: string; mimeType?: string }>,
    mimeType = 'image/jpeg'
  ) => {
    let body: any;
    if (Array.isArray(imagesInput)) {
      body = {
        images: imagesInput.map((img) => ({
          image_base64: img.base64,
          mime_type: img.mimeType || 'image/jpeg',
        })),
      };
    } else {
      body = { image_base64: imagesInput, mime_type: mimeType };
    }

    const res = await request<{ success: boolean; result: OcrShiftReportResult }>('/api/ocr/shift-report', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return res.result;
  },

  reconcileShift: async (payload: {
    shift_id?: string;
    shift_date?: string;
    sales: ShiftSalesItem[];
    waste?: ShiftWasteItem[];
    actual_stock?: ShiftStockCountItem[];
    actual_raw_stock?: ShiftStockCountItem[];
    actual_prepared_stock?: ShiftStockCountItem[];
    auto_balance?: boolean;
    loss_tolerance_pct?: number;
    financial_loss_threshold?: number;
    note?: string;
  }) => {
    const res = await request<{ success: boolean; result: ShiftReconciliationResult }>('/api/shifts/reconcile', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return res.result;
  },

  // Flow 4: Báo Hủy / Hỏng
  scanWasteOcr: async (imageBase64: string, mimeType = 'image/jpeg') => {
    const res = await request<{ success: boolean; result: OcrWasteResult }>('/api/ocr/waste', {
      method: 'POST',
      body: JSON.stringify({ image_base64: imageBase64, mime_type: mimeType }),
    });
    return res.result;
  },

  recordWaste: async (
    items: Array<{ item_name: string; quantity: number; unit?: string; reason?: string }>,
    warehouse: 'STORE' | 'CENTRAL' = 'STORE',
    note?: string
  ) => {
    const res = await request<{ success: boolean; result: any }>('/api/inventory/waste', {
      method: 'POST',
      body: JSON.stringify({ items, warehouse, note }),
    });
    return res.result;
  },

  // Flow 5: Cân bằng tồn kho định kỳ
  scanInventoryBalanceOcr: async (imageBase64: string, mimeType = 'image/jpeg') => {
    const res = await request<{ success: boolean; result: OcrInventoryBalanceResult }>('/api/ocr/inventory-balance', {
      method: 'POST',
      body: JSON.stringify({ image_base64: imageBase64, mime_type: mimeType }),
    });
    return res.result;
  },

  adjustInventoryBalance: async (
    warehouse: 'STORE' | 'CENTRAL',
    items: Array<{ ingredient_name: string; actual_quantity: number; note?: string }>,
    note?: string
  ) => {
    const res = await request<{ success: boolean; result: any }>('/api/inventory/balance', {
      method: 'POST',
      body: JSON.stringify({ warehouse, items, note }),
    });
    return res.result;
  },

  getShiftReports: async () => {
    const res = await request<{ success: boolean; data: any[] }>('/api/shifts/reports');
    return res.data;
  },
};
